import { Form, Formik, FormikActions, FormikErrors } from 'formik';
import debounce from 'lodash/debounce';
import uniq from 'lodash/uniq';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { compose } from 'recompose';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import getSubdomainFromUrl from 'utils/getSubdomainFromUrl';
import { useSubDomainInfo_subDomain } from 'LoggedOut/SignUp/__generated__/useSubDomainInfo';
import { usePractitionerCountries } from 'Talk/hooks/usePractitionerCountries';
import { useUnmindTalk } from 'Talk/hooks';
import { useSubdomainInfo } from '../../LoggedOut/SignUp/useSubdomainInfo';
import { tracking } from '../../App/Tracking';
import { LocalGeneralError } from '../../Shared/AlertView';
import { Errors } from '../../Shared/Errors';
import { GridItem } from '../../Shared/Grid';
import LoadingIndicator, {
  withLoadingIndicator,
} from '../../Shared/LoadingIndicator';
import { ButtonSize } from '../../Shared/Button';
import Tooltip, { useTooltipState } from '../../Shared/Tooltip';
import { isEmployee, isPlusOne } from '../../utils';
import withUpdateUser, { UpdateUserInput } from '../withUpdateUser';
import {
  useUser,
  useSubdomainDivisions,
  useUserLocation,
  countryRequiresState,
  CANADA,
} from '../hooks';
import { changeUserDetails } from './helper';
import * as S from './Profile.styles';
import InputWrapper from './components/InputWrapper';
import AlertGridItemWrapper from './components/AlertGridItemWrapper';
import SelectWrapper from './components/SelectWrapper';

const trackTooltip = debounce(() => {
  tracking.track('settings-plus-one-email-tooltip-clicked');
}, 1000);

export interface ProfileProps {
  confirmEmailUpdateSuccess: boolean;
  confirmEmailUpdateError: boolean;
  updateUser(inputProps: UpdateUserInput): Promise<{
    success: boolean;
    error?: Errors;
  }>;
}

interface ProfileOutProps {
  confirmEmailUpdateSuccess: boolean;
  confirmEmailUpdateError: boolean;
}

export interface FormFields {
  [property: string]: boolean | string | undefined;
  firstName: string;
  lastName: string;
  praiseDirectory?: boolean;
  emailAddress: string;
  initialEmailAddress: string;
  location?: string;
  department?: string;
  country?: string;
  state?: string;
}

const checkIsEmailOrganisation = (
  groupFileFields: useSubDomainInfo_subDomain['groupFileFields'] | undefined,
) => {
  if (Array.isArray(groupFileFields) && groupFileFields.length > 0) {
    return groupFileFields.some(
      groupFileField => groupFileField?.fileMappingField === 'email',
    );
  }

  return true;
};

export const Profile: React.FC<ProfileProps> = ({
  confirmEmailUpdateSuccess,
  confirmEmailUpdateError,
  updateUser,
}) => {
  const {
    firstName = '',
    lastName = '',
    email = '',
    praiseDirectory = true,
    locationId,
    departmentId,
    userType,
    loading: loadingUser,
    error: userLoadingError,
  } = useUser();
  const subdomain = getSubdomainFromUrl();
  const {
    locations,
    departments,
    loading: loadingSubdomainDivisions,
  } = useSubdomainDivisions(subdomain);
  const formikRef = useRef<Formik<FormFields>>(null);
  const { t: translate } = useTranslation('account');

  const [message, setMessage] = useState(
    translate('profile.messages.default_success'),
  );
  const { open: tooltipOpen, handlers: tooltipHandlers } = useTooltipState();

  //for now all users authenticated via auth0 are SSO users, we will revist this after auth0 migration work
  const { isAuthenticated: isAuthenticatedViaAuth0, isLoading: loadingAuth0 } =
    useAuth0();
  const { groupFileFields, loading: loadingSubdomainInfo } = useSubdomainInfo({
    subdomain,
  });

  const {
    userLocationData,
    hasValidLocationData,
    requiresState,
    loading: loadingLocationData,
  } = useUserLocation();

  const {
    countryData,
    getStateData,
    loading: loadingPractitionerCountry,
  } = usePractitionerCountries();

  const { unmindTalkIsAvailableForUser, loading: loadingTalkEntitlements } =
    useUnmindTalk();

  useEffect(() => {
    if (confirmEmailUpdateSuccess) {
      tracking.track('email-reset-confirmed');
      setMessage(translate('profile.messages.confirm_email_update_success'));
    }

    if (confirmEmailUpdateError) {
      setMessage(translate('profile.messages.confirm_email_update_error'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (
    loadingSubdomainInfo ||
    loadingAuth0 ||
    loadingSubdomainDivisions ||
    loadingUser ||
    loadingLocationData ||
    loadingPractitionerCountry ||
    loadingTalkEntitlements
  ) {
    return <LoadingIndicator />;
  }

  if (userLoadingError) {
    return <LocalGeneralError />;
  }

  const isEmailOrganisation = checkIsEmailOrganisation(groupFileFields);

  const isPlusOneUser = isPlusOne(userType);

  const initialValues: FormFields = {
    firstName,
    lastName,
    emailAddress: email,
    initialEmailAddress: email,
    praiseDirectory,
    ...(locationId ? { location: locationId } : undefined),
    ...(departmentId ? { department: departmentId } : undefined),
    ...(hasValidLocationData && userLocationData?.user?.country?.value
      ? { country: userLocationData?.user?.country?.value }
      : undefined),
    ...(hasValidLocationData &&
    requiresState &&
    userLocationData?.user?.state?.value
      ? { state: userLocationData?.user?.state?.value }
      : undefined),
  };

  const onSubmit = async (
    formFields: FormFields,
    actions: Pick<
      FormikActions<FormFields>,
      'setStatus' | 'setSubmitting' | 'resetForm'
    >,
  ) => {
    actions.setSubmitting(true);
    actions.setStatus('');

    const response = await changeUserDetails({
      updateUser,
      ...formFields,
      state:
        formFields.country && countryRequiresState(formFields.country)
          ? formFields.state
          : null,
      praiseDirectory: isPlusOneUser ? undefined : formFields.praiseDirectory,
    });
    actions.resetForm(formFields);

    if (!response.success) {
      actions.setStatus('failed');
    } else {
      if (response.emailAddressUpdate) {
        tracking.track('email-reset-request');
      }

      tracking.track('settings-changes-saved', {
        settingsField: uniq([
          ...Object.entries(initialValues).reduce<string[]>(
            (accumulator, [key, value]) =>
              formFields[key] === value ? accumulator : [...accumulator, key],
            [],
          ),
          ...Object.entries(formFields).reduce<string[]>(
            (accumulator, [key, value]) =>
              initialValues[key] === value
                ? accumulator
                : [...accumulator, key],
            [],
          ),
        ]),
      });

      actions.setStatus('success');
    }

    const getUpdateUserDetailsMessage = () => {
      if (!response.success) {
        return translate('profile.messages.failed');
      }

      return response.emailAddressUpdate
        ? translate('profile.messages.email_update_success', {
            email: formFields.emailAddress,
          })
        : translate('profile.messages.default_success');
    };

    setMessage(getUpdateUserDetailsMessage());
  };

  const validateForm = (formValues: FormFields) => {
    const errors: FormikErrors<FormFields> = {};

    if (
      formValues.country &&
      countryRequiresState(formValues.country) &&
      !formValues.state
    ) {
      errors.state =
        formValues.country === CANADA
          ? translate('profile.validation.province')
          : translate('profile.validation.state');
    }

    return errors;
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      ref={formikRef}
      validateOnBlur
      validate={validateForm}
    >
      {({ dirty, isSubmitting, isValid, status, values }) => (
        <Form>
          <S.StyledGrid>
            <AlertGridItemWrapper message={message} status={status} />
            <InputWrapper
              id="firstName"
              translationKey="first_name"
              value={values.firstName}
            />
            <InputWrapper
              id="lastName"
              translationKey="last_name"
              value={values.lastName}
            />

            <InputWrapper
              id="emailAddress"
              translationKey="email"
              value={values.emailAddress}
              disabled={
                isPlusOneUser ||
                (isAuthenticatedViaAuth0 && isEmailOrganisation)
              }
              tooltip={
                isPlusOneUser ? (
                  <Tooltip
                    labelCopy={translate('profile.plus_one_user.email_tooltip')}
                    placement="right"
                    open={tooltipOpen}
                    accessibilityId="disabled-plusone-email-tooltip"
                    data-testid="settings-plusone-email-tooltip"
                    onMouseEnter={() => {
                      trackTooltip();
                      tooltipHandlers.onMouseEnter();
                    }}
                  >
                    <S.InfoIcon />
                  </Tooltip>
                ) : null
              }
            />

            {isEmployee(userType) ? (
              <Fragment>
                {locations && locations.length > 0 ? (
                  <SelectWrapper
                    translationKey="location"
                    options={locations.map(({ name, id }) => ({
                      label: name,
                      value: id,
                    }))}
                  />
                ) : null}

                {departments && departments.length > 0 ? (
                  <SelectWrapper
                    translationKey="department"
                    options={departments.map(({ name, id }) => ({
                      label: name,
                      value: id,
                    }))}
                  />
                ) : null}
              </Fragment>
            ) : (
              <GridItem fullWidth>
                <S.SectionSeparator />
              </GridItem>
            )}

            {unmindTalkIsAvailableForUser ? (
              <Fragment>
                {countryData.length > 0 ? (
                  <SelectWrapper
                    translationKey="country"
                    options={countryData}
                    onChange={value => {
                      formikRef.current?.setFieldValue('country', value);
                      formikRef.current?.setFieldValue(
                        'state',
                        undefined,
                        true,
                      );
                    }}
                  />
                ) : null}

                {values.country && countryRequiresState(values.country) ? (
                  <SelectWrapper
                    name="state"
                    onChange={value =>
                      formikRef.current?.setFieldValue('state', value)
                    }
                    translationKey={
                      values.country === CANADA ? 'province' : 'state'
                    }
                    options={getStateData(values.country)}
                  />
                ) : null}
              </Fragment>
            ) : null}
            <GridItem fullWidth>
              <S.SubmitButton
                disabled={!dirty || !isValid}
                ariaLabel={translate('profile.save_button.a11y_label')}
                label={translate('profile.save_button.label')}
                loading={isSubmitting}
                size={ButtonSize.medium}
                type="submit"
              />
            </GridItem>
          </S.StyledGrid>
        </Form>
      )}
    </Formik>
  );
};

export default compose<ProfileProps, ProfileOutProps>(
  withLoadingIndicator,
  withUpdateUser,
)(Profile);
